/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/order
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { graphqlMoneyBagField } from "./constants";
import {
  ShopifyDutyKey,
  ShopifyDutyToOrderLineItemKey,
  ShopifyExchangeLineItemKey,
  ShopifyExchangeLineItemToOrderLineItemKey,
  ShopifyFulfillmentKey,
  ShopifyFulfillmentLineItemKey,
  ShopifyFulfillmentLineItemToOrderLineItemKey,
  ShopifyFulfillmentOrderLineItemKey,
  ShopifyFulfillmentOrderLineItemToOrderLineItemKey,
  ShopifyGiftCardKey,
  ShopifyGiftCardToOrderLineItemKey,
  ShopifyLocationKey,
  ShopifyOrderKey,
  ShopifyOrderLineItemKey,
  ShopifyOrderLineItemToDutiesKey,
  ShopifyOrderLineItemToExchangeLineItemsKey,
  ShopifyOrderLineItemToFulfillmentLineItemsKey,
  ShopifyOrderLineItemToFulfillmentOrderLineItemsKey,
  ShopifyOrderLineItemToGiftCardsKey,
  ShopifyOrderLineItemToOrderKey,
  ShopifyOrderLineItemToOriginLocationKey,
  ShopifyOrderLineItemToProductKey,
  ShopifyOrderLineItemToProductVariantKey,
  ShopifyOrderLineItemToRefundLineItemsKey,
  ShopifyOrderLineItemToShopKey,
  ShopifyProductKey,
  ShopifyProductVariantKey,
  ShopifyRefundLineItemKey,
  ShopifyRefundLineItemToOrderLineItemKey,
  ShopifyShopKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// we added shopify field keys in 2025-04 which changes how the static keys are generated. these are here so that they remain the same
const discountedTotalSetKey = "ModelField-DataModel-Shopify-OrderLineItem-discountedTotalSet";
const discountedTotalSetStorageEpochKey = "FieldStorageEpoch-DataModel-Shopify-OrderLineItem-discountedTotalSet-initial";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyOrderLineItemKey,
  defaultFilterableApiIdentifiers: ["currentQuantity"],
  "2022-01": {
    name: "Shopify Order Line Item",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToOrderKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "order_id" } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToProductVariantKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_id" } },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToProductKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "product_id" } },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Origin Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToOriginLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "origin_location" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
      },
      {
        name: "Duties",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "duties" } },
        key: ShopifyOrderLineItemToDutiesKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyDutyKey,
          inverseFieldKey: ShopifyDutyToOrderLineItemKey,
        },
      },
      {
        name: "Fulfillment Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToFulfillmentLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentLineItemKey,
          inverseFieldKey: ShopifyFulfillmentLineItemToOrderLineItemKey,
        },
      },
      {
        name: "Fulfillment Order Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToFulfillmentOrderLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyFulfillmentOrderLineItemToOrderLineItemKey,
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "quantity" } },
      },
      {
        name: "Refunds",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToRefundLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyRefundLineItemKey,
          inverseFieldKey: ShopifyRefundLineItemToOrderLineItemKey,
        },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sku" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title" } },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_title" } },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "vendor" } },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "name" } },
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "gift_card" } },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "price_set" } },
      },
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "properties" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "taxable" } },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tax_lines" } },
      },
      {
        name: "Total Discount",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_discount" } },
      },
      {
        name: "Total Discount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "total_discount_set" } },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "discount_allocations" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: "order" },
  },
  "2023-04": {
    fields: [
      {
        name: "Fulfillable Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillable_quantity" } },
      },
      {
        name: "Fulfillment Service",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillment_service" } },
      },
      {
        name: "Fulfillment Status",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "fulfillment_status" } },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "grams" } },
      },
      {
        name: "Product Exists",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "product_exists" } },
      },
      {
        name: "Variant Inventory Management",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "variant_inventory_management" } },
      },
    ],
  },
  "2023-07": {
    fields: [
      {
        name: "Attributed Staffs",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "attributed_staffs" } },
      },
    ],
  },
  "2024-01": {
    fields: [
      {
        name: "Current Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "current_quantity" } },
      },
    ],
  },
  "2024-07": {
    fields: [
      {
        name: "Exchange Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToExchangeLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyExchangeLineItemKey,
          inverseFieldKey: ShopifyExchangeLineItemToOrderLineItemKey,
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id", availableInWebhook: ["orders/create", "orders/updated"] },
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "order_id", graphQLFieldKey: "orderId", skipGraphQLSelection: true },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Variant",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToProductVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_id",
            graphQLFieldKey: "variant",
            graphQLSelection: "id",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
        },
      },
      {
        name: "Product",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToProductKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "product_id",
            graphQLFieldKey: "product",
            graphQLSelection: "id",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductKey,
        },
      },
      {
        name: "Origin Location",
        fieldType: FieldType.BelongsTo,
        key: ShopifyOrderLineItemToOriginLocationKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "origin_location" } },
        configuration: {
          relatedModelKey: ShopifyLocationKey,
        },
        isDeleted: true,
      },
      {
        name: "Duties",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToDutiesKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["orders/create", "orders/updated"],
            shopifyFieldKey: "duties",
            graphQLFieldKey: "duties",
            graphQLSelection: `
          id
          countryCodeOfOrigin
          harmonizedSystemCode
          price {
            shopMoney {
              amount
              currencyCode
            }
            presentmentMoney {
              amount
              currencyCode
            }
          }
          taxLines {
            channelLiable
            priceSet {
              shopMoney {
                amount
                currencyCode
              }
              presentmentMoney {
                amount
                currencyCode
              }
            }
            rate
            ratePercentage
            source
            title
            price
          }
          `,
            selectForHasMany: true,
          },
        },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyDutyKey,
          inverseFieldKey: ShopifyDutyToOrderLineItemKey,
        },
      },
      {
        name: "Fulfillment Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToFulfillmentLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentLineItemKey,
          inverseFieldKey: ShopifyFulfillmentLineItemToOrderLineItemKey,
        },
      },
      {
        name: "Fulfillment Order Line Items",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToFulfillmentOrderLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyFulfillmentOrderLineItemToOrderLineItemKey,
        },
      },
      {
        name: "Price",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "price",
            graphQLFieldKey: "price",
            skipGraphQLSelection: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "quantity",
            graphQLFieldKey: "quantity",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Refunds",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToRefundLineItemsKey,
        configuration: {
          relatedModelKey: ShopifyRefundLineItemKey,
          inverseFieldKey: ShopifyRefundLineItemToOrderLineItemKey,
        },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "requires_shipping",
            graphQLFieldKey: "requiresShipping",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "sku", graphQLFieldKey: "sku", availableInWebhook: ["orders/create", "orders/updated"] },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "title",
            graphQLFieldKey: "title",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Variant Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_title",
            graphQLFieldKey: "variantTitle",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Vendor",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "vendor",
            graphQLFieldKey: "vendor",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Name",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "name",
            graphQLFieldKey: "name",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Gift Card",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "gift_card",
            graphQLFieldKey: "isGiftCard",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["orders/create", "orders/updated"],
            shopifyFieldKey: "price_set",
            graphQLFieldKey: "originalUnitPriceSet",
            graphQLSelection: `
          shopMoney {
            amount
            currencyCode
          }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Properties",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["orders/create", "orders/updated"],
            shopifyFieldKey: "properties",
            graphQLFieldKey: "customAttributes",
            graphQLSelection: `
          key
          value
          `,
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at" } },
        isDeleted: true,
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
        isDeleted: true,
      },
      {
        name: "Taxable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "taxable",
            graphQLFieldKey: "taxable",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Tax Lines",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["orders/create", "orders/updated"],
            shopifyFieldKey: "tax_lines",
            graphQLFieldKey: "taxLines",
            graphQLSelection: `
          channelLiable
          priceSet {
            shopMoney {
              amount
              currencyCode
            }
            presentmentMoney {
              amount
              currencyCode
            }
          }
          rate
          ratePercentage
          source
          title
          price
          `,
          },
        },
      },
      {
        name: "Total Discount",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "total_discount",
            graphQLFieldKey: "totalDiscount",
            skipGraphQLSelection: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Total Discount Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["orders/create", "orders/updated"],
            shopifyFieldKey: "total_discount_set",
            graphQLFieldKey: "totalDiscountSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Discount Allocations",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            availableInWebhook: ["orders/create", "orders/updated"],
            shopifyFieldKey: "discount_allocations",
            graphQLFieldKey: "discountAllocations",
            graphQLSelection: `
          allocatedAmountSet {
            shopMoney {
              amount
              currencyCode
            }
            presentmentMoney {
              amount
              currencyCode
            }
          }
          discountApplication {
            allocationMethod
            index
            targetSelection
            targetType
            value
          }
          `,
          },
        },
      },
      {
        name: "Fulfillable Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "fulfillable_quantity",
            graphQLFieldKey: "fulfillableQuantity",
            deprecated: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Fulfillment Service",
        fieldType: FieldType.String,
        shouldConvergeField: (enabledModelKeys) => enabledModelKeys.includes(ShopifyFulfillmentKey),
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "fulfillment_service",
            graphQLFieldKey: "fulfillmentService",
            graphQLSelection: "handle",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Fulfillment Status",
        fieldType: FieldType.String,
        shouldConvergeField: (enabledModelKeys) => enabledModelKeys.includes(ShopifyFulfillmentKey),
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "fulfillment_status",
            graphQLFieldKey: "fulfillmentStatus",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Grams",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "grams", deprecated: true, availableInWebhook: ["orders/create", "orders/updated"] },
        },
      },
      {
        name: "Product Exists",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "product_exists",
            deprecated: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Variant Inventory Management",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "variant_inventory_management",
            deprecated: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Attributed Staffs",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "attributed_staffs",
            deprecated: true,
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Current Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "current_quantity",
            graphQLFieldKey: "currentQuantity",
            availableInWebhook: ["orders/create", "orders/updated"],
          },
        },
      },
      {
        name: "Discounted Total Set",
        key: discountedTotalSetKey,
        initialStorageEpochKey: discountedTotalSetStorageEpochKey,
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "discounted_total_set",
            graphQLFieldKey: "discountedTotalSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Discounted Unit Price After All Discounts Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLOnlyField: true,
            graphQLFieldKey: "discountedUnitPriceAfterAllDiscountsSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Discounted Unit Price Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLOnlyField: true,
            graphQLFieldKey: "discountedUnitPriceSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Original Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLOnlyField: true,
            graphQLFieldKey: "originalTotalSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Unfulfilled Discounted Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLOnlyField: true,
            graphQLFieldKey: "unfulfilledDiscountedTotalSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Unfulfilled Original Total Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLOnlyField: true,
            graphQLFieldKey: "unfulfilledOriginalTotalSet",
            graphQLSelection: `
          shopMoney {
              amount
              currencyCode
            }
          presentmentMoney {
            amount
            currencyCode
          }
          `,
          },
        },
      },
      {
        name: "Merchant Editable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "merchantEditable", graphQLOnlyField: true } },
      },
      {
        name: "Non Fulfillable Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "nonFulfillableQuantity", graphQLOnlyField: true } },
      },
      {
        name: "Refundable Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "refundableQuantity", graphQLOnlyField: true } },
      },
      {
        name: "Restockable",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "restockable", graphQLOnlyField: true } },
      },
      {
        name: "Unfulfilled Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "unfulfilledQuantity", graphQLOnlyField: true } },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "orders",
      resource: "order.lineItems",
    },
  },
  "2026-07": {
    fields: [
      {
        name: "Price After All Discounts Before Taxes Set",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            // GraphQL-only: order webhook payloads expose current_subtotal_price_set at the
            // order level only, never on line items, so this field can't be webhook-populated
            graphQLFieldKey: "priceAfterAllDiscountsBeforeTaxesSet",
            graphQLOnlyField: true,
            graphQLSelection: graphqlMoneyBagField,
          },
        },
      },
      {
        name: "Weight",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "weight",
            graphQLOnlyField: true,
            graphQLSelection: `
          unit
          value
          `,
          },
        },
      },
      {
        // Inverse of GiftCard.lineItem (new in 2026-07): the gift cards created by selling this line item
        name: "Gift Cards",
        fieldType: FieldType.HasMany,
        key: ShopifyOrderLineItemToGiftCardsKey,
        configuration: {
          relatedModelKey: ShopifyGiftCardKey,
          inverseFieldKey: ShopifyGiftCardToOrderLineItemKey,
        },
      },
    ],
  },
};

export const ShopifyOrderLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
